import React, { useEffect, useState } from "react";
import { api, ROLES, STATUSES } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { Save, Plus, X, ArrowRight, ScanLine } from "lucide-react";

const STAFF_ROLES = ROLES.filter((r) => r.value !== "admin");

export const ScanRulesTab = ({ all, save }) => {
  const [v, setV] = useState(all.scan_rules || {});
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState({});
  useEffect(() => { setV(all.scan_rules || {}); }, [all]);

  const setFlow = (role, flow) => setV((cur) => { const n = { ...cur }; if (flow.length) n[role] = flow; else delete n[role]; return n; });
  const doSave = async () => {
    setSaving(true);
    try { const { data } = await api.put("/settings/scan_rules", { value: v }); await save("scan_rules", data); toast.success("Scan rules saved"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  return (
    <Card className="p-5 space-y-4" data-testid="sys-scan-rules">
      <p className="text-xs text-muted-foreground flex items-start gap-1.5"><ScanLine className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        When a staff member with one of these roles scans a Job Sheet / Thermal Label QR, the job moves automatically along the sequence: 1st scan → first status, next scan → next status. Roles without a rule get the manual status buttons.</p>
      {STAFF_ROLES.map((r) => {
        const flow = v[r.value] || [];
        const remaining = STATUSES.filter((s) => !flow.includes(s));
        return (
          <div key={r.value} className="border rounded-lg p-3 space-y-2" data-testid={`sys-scan-role-${r.value}`}>
            <div className="flex items-center justify-between">
              <Label className="font-semibold">{r.label}</Label>
              <span className="text-[11px] font-mono uppercase text-muted-foreground">{flow.length ? "auto-update on scan" : "manual buttons"}</span>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              {flow.map((s, i) => (
                <React.Fragment key={s}>
                  {i > 0 && <ArrowRight className="w-3.5 h-3.5 text-muted-foreground" />}
                  <span className="inline-flex items-center gap-1" data-testid={`sys-scan-step-${r.value}-${i}`}>
                    <StatusBadge status={s} />
                    <button type="button" onClick={() => setFlow(r.value, flow.filter((x) => x !== s))} className="text-muted-foreground hover:text-rose-600" data-testid={`sys-scan-remove-${r.value}-${i}`}><X className="w-3.5 h-3.5" /></button>
                  </span>
                </React.Fragment>
              ))}
              {remaining.length > 0 && (
                <div className="flex items-center gap-1 ml-1">
                  <Select value={adding[r.value] || ""} onValueChange={(s) => setAdding({ ...adding, [r.value]: s })}>
                    <SelectTrigger className="h-8 w-44 text-xs" data-testid={`sys-scan-select-${r.value}`}><SelectValue placeholder="Add step…" /></SelectTrigger>
                    <SelectContent>{remaining.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button size="sm" variant="outline" className="h-8" disabled={!adding[r.value]} onClick={() => { setFlow(r.value, [...flow, adding[r.value]]); setAdding({ ...adding, [r.value]: "" }); }} data-testid={`sys-scan-add-${r.value}`}><Plus className="w-3.5 h-3.5" /></Button>
                </div>
              )}
            </div>
          </div>
        );
      })}
      <div className="flex justify-end"><Button onClick={doSave} disabled={saving} className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="sys-scan-save"><Save className="w-4 h-4 mr-1" /> Save</Button></div>
    </Card>
  );
};
